import { Link } from 'react-router-dom'
import { featuredModuleCards, moreModuleCards, MODULE_THEME } from '@/data/modules'
import type { ModulePage } from '@/data/modules'
import { SignupCta } from '@/components/ui'
import { ModuleTierBadge } from '@/components/pricing'
import { SEO } from '@/components/seo'
import ModuleIcon from './ModuleIcon'

function ModuleCard({ module, large }: { module: ModulePage; large?: boolean }) {
  const theme = MODULE_THEME[module.colorClass]
  return (
    <Link
      to={`/product/${module.slug}`}
      className="group flex flex-col bg-white border border-slate-200 rounded-2xl p-7 shadow-card no-underline transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-card-hover"
    >
      <div
        className={`${large ? 'w-14 h-14' : 'w-12 h-12'} rounded-xl ${theme.iconBg} ${theme.accentText} flex items-center justify-center mb-5`}
      >
        <ModuleIcon name={module.icon} className={large ? 'w-7 h-7' : 'w-6 h-6'} />
      </div>
      <h3 className={`font-serif ${large ? 'text-2xl' : 'text-xl'} font-medium text-slate-900 mb-2`}>
        {module.name}
      </h3>
      <p className="text-[15px] text-slate-600 leading-relaxed mb-5 flex-1">{module.tagline}</p>
      <div className="flex items-center justify-between gap-3">
        <ModuleTierBadge
          pricingModules={module.pricingModules}
          variant="card"
          colorClass={module.colorClass}
        />
        <span className={`inline-flex items-center gap-1 text-sm font-semibold ${theme.accentText}`}>
          Explore
          <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  )
}

export default function ProductHubPage() {
  return (
    <>
      <SEO
        title="Product: Every Module, Every Tier | WealthSpace"
        description="Explore every WealthSpace module (meeting capture, AI reports, the Sparks assistant, Ramble dictation, client management and more) with use cases, benefits, and the plan each one is included in."
        keywords="WealthSpace modules, financial adviser software features, IFA platform modules, adviser tools by plan"
        url="/product"
      />

      {/* Hero */}
      <section className="pt-24 pb-12 px-6 text-center">
        <div className="max-w-[760px] mx-auto">
          <p className="text-xs font-semibold text-accent-teal tracking-[0.15em] uppercase mb-4">
            The platform
          </p>
          <h1 className="font-serif text-[clamp(36px,5vw,54px)] leading-[1.12] text-slate-900 mb-5">
            One platform. Every part of your practice.
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mb-7">
            Explore each WealthSpace module: what it does, where you'd use it, and which plan it's
            included in. Mix the modules your firm needs.
          </p>
          <Link
            to="/product/explore"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-teal no-underline hover:text-accent-tealDark"
          >
            Explore the five core modules
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Featured modules */}
      <section className="pb-16 px-6">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredModuleCards.map((module) => (
            <ModuleCard key={module.slug} module={module} large />
          ))}
        </div>
      </section>

      {/* More modules */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-[clamp(26px,3vw,34px)] font-normal text-slate-900 mb-3">
              More modules
            </h2>
            <p className="text-slate-600 max-w-[560px] mx-auto">
              The rest of the toolkit (from planning tools to compliance) ready when your firm
              needs them.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {moreModuleCards.map((module) => (
              <ModuleCard key={module.slug} module={module} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-[640px] mx-auto text-center">
          <h2 className="font-serif text-[clamp(28px,3.5vw,38px)] text-slate-900 mb-4">
            See it on your own practice
          </h2>
          <p className="text-slate-600 mb-8">
            Start free, pick the modules you need, and have your first report or file note done
            today.
          </p>
          <SignupCta location="product_hub_cta">
            Start Free Trial
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </SignupCta>
        </div>
      </section>
    </>
  )
}
